import { useCallback, useEffect, useMemo, useState } from 'react'

import { Dropdown, Menu } from 'antd'
import { isArray } from 'lodash'
import { useSelector } from 'react-redux'
import { faFileExport, faList } from '@fortawesome/pro-solid-svg-icons'

import Analytics from '../../../utils/analytics'
import StudentsTable, { getStudentActiveKeys } from './StudentsTable'
import { selectUser } from '../../../reducers/UserReducer'
import { StudentProperties } from '../../../utils/entities/student'
import { OrderDirections } from '../../../utils/constants'
import { useLocalStorage } from '../../../hooks/UseLocalStorage'
import { retrieveMetadata } from '../../../utils/http'
import { exportStudents, getAllStudents } from '../../../utils/api/student'
import LOCAL_STORAGE_KEYS from '../../../utils/localStorage'
import { TableParameters } from '../../../utils/entities/tableParameters'
import StudentActionsProvider from '../../../Providers/Student/StudentActions'
import DefaultContainer from '../../../HOC/DefaultContainer'
import IconLabel from '../../../Components/icons/IconLabel'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import TooltipIconButton from '../../../Components/antd/Buttons/TooltipIconButton'
import { onError, onInfo } from '../../../utils/apiHelper'
import { selectActiveLanguageCode } from '../../../reducers/LanguageReducer'
import { getDocument } from '../../../utils/api/export'

const { EMAIL } = StudentProperties

const StudentsView = () => {
  const currentLanguage = useSelector(selectActiveLanguageCode)
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [students, setStudents] = useState({ data: [], loading: false, metadata: null })
  const [parameters, setParameters] = useLocalStorage(
    LOCAL_STORAGE_KEYS.STUDENTS_VIEW.LIST_PARAMETERS,
    new TableParameters({ order: { by: EMAIL, direction: OrderDirections.ASC }, pageSize: 50, filters: { columns: getStudentActiveKeys(user) } })
  )

  const parametersWithMeta = useMemo(() => new TableParameters({ ...parameters, metadata: students.metadata }), [parameters, students.metadata])

  const fetchStudents = useCallback(parameters => {
    setStudents(prev => ({ ...prev, loading: true }))

    getAllStudents(user, { ...parameters.toQueryParameters(), contexts: ['niss'] }).then(({ data, meta }) => {
      setStudents({ data, loading: false, metadata: retrieveMetadata(meta) })
    })
  }, [user?.id])

  useEffect(() => Analytics.pageView('/institution-students'), [])
  useEffect(() => {
    fetchStudents(new TableParameters({ ...parameters }))
  }, [fetchStudents, parameters])

  const handleStudentsExport = () => {
    onInfo(t('common.start_export'))
    exportStudents(user, {
      order: parameters.order,
      language: currentLanguage,
      filters: { ...parameters.filters, columns: ['id', ...(isArray(parameters.filters.columns) ? parameters.filters.columns : [])] }
    }).then(({ job }) => getDocument(job, user, () => onError(t('spreadsheet_download.error'))
    )).catch(error => {
      console.error(error.message)
      onError(t('internships_export.error'))
    })
  }

  const exporter = () => (
    <Dropdown
      trigger={['click']}
      overlay={
        <Menu>
          <Menu.Item onClick={handleStudentsExport}>
            <IconLabel icon={faList} text={t('students_view.export.list')} />
          </Menu.Item>
        </Menu>
      }
    >
      <TooltipIconButton icon={faFileExport} title={t('Export')} />
    </Dropdown>
  )

  return (
    <DefaultContainer>
      <StudentActionsProvider>
        <StudentsTable
          data={students.data}
          exporter={exporter}
          loading={students.loading}
          parameters={parametersWithMeta}
          onFiltersChange={filters => setParameters(prev => new TableParameters({ ...prev, filters, page: 1 }))}
          onKeysChanged={keys => setParameters(prev => new TableParameters({ ...prev, filters: { ...prev.filters, columns: keys }, page: 1 }))}
          onParametersChange={parameters => setParameters(prev => new TableParameters({
            ...prev, ...parameters, page: parameters.search !== prev.search ? 1 : parameters.page
          }))}
        />
      </StudentActionsProvider>
    </DefaultContainer>
  )
}

export default StudentsView
